/**
 * ErrorDialog
 *
 * 실패를 화면 중앙 모달로 알린다.
 *
 * @why 기존에는 실패를 페이지 상단 인라인 배너로만 보여줬다. 일괄 입력처럼
 * 스크롤이 길고 실행 버튼이 하단에 고정된 화면에서는, 배너가 화면 밖에 그려져
 * 사용자에게는 "아무 일도 일어나지 않은 것"으로 보인다.
 * 모달은 스크롤 위치와 무관하게 반드시 보이므로 실패가 조용히 묻히지 않는다.
 *
 * 진단 정보(details)를 함께 두는 이유: 태블릿·모바일에는 개발자 도구가 없어
 * console 로그를 볼 방법이 없다. 화면에서 바로 복사하거나 캡처할 수 있어야
 * 사용자가 상황을 전달할 수 있다.
 */

import { useState } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'

export interface ErrorDialogProps {
  isOpen: boolean
  onClose: () => void
  /** 무엇이 실패했는지 (예: "거래 저장 실패") */
  title?: string
  /** 사용자가 읽고 행동할 수 있는 문장 */
  message: string
  /**
   * 추가 안내. 되돌리기 어려운 부작용이 있을 수 있을 때 경고로 쓴다.
   * (예: "이미 저장됐을 수 있으니 목록을 먼저 확인하세요")
   */
  warning?: string
  /** 개발자용 진단 정보. 접어둔 채로 제공하고 복사할 수 있게 한다 */
  details?: string
  /** 재시도 버튼. 주지 않으면 닫기만 노출한다 */
  onRetry?: () => void
  retryLabel?: string
}

export function ErrorDialog({
  isOpen,
  onClose,
  title = '오류가 발생했습니다',
  message,
  warning,
  details,
  onRetry,
  retryLabel = '다시 시도',
}: ErrorDialogProps) {
  const [showDetails, setShowDetails] = useState(false)
  const [copied, setCopied] = useState(false)

  /**
   * 진단 정보 복사
   *
   * @why clipboard API는 보안 컨텍스트(HTTPS)에서만 동작하고 권한 거부도 가능하다.
   * 실패해도 아래 <pre>에 원문이 그대로 보이므로 사용자는 길게 눌러 선택하거나
   * 화면을 캡처할 수 있다. 그래서 실패를 조용히 삼켜도 정보 전달 경로는 남는다.
   */
  const handleCopy = async () => {
    if (!details) return

    try {
      await navigator.clipboard.writeText(details)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // 복사만 실패했을 뿐 원문은 화면에 그대로 남아 있다
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <p className="text-base text-gray-800 dark:text-gray-100 whitespace-pre-line">
          {message}
        </p>

        {warning && (
          <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            {warning}
          </p>
        )}

        {details && (
          <div>
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              className="text-sm text-gray-500 dark:text-gray-400 underline min-h-12"
            >
              {showDetails ? '자세한 내용 숨기기' : '자세한 내용 보기'}
            </button>

            {showDetails && (
              <div className="mt-2 space-y-2">
                <pre className="text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all select-all">
                  {details}
                </pre>
                <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
                  {copied ? '복사됨' : '진단 정보 복사'}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            닫기
          </Button>
          {onRetry && (
            <Button type="button" variant="primary" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
